import { fuelKindForType } from "./catalogs";
import { dualClockStamp } from "./clock";
import { crewIdFromName } from "./duty";
import {
  applyLogStatus,
  blockTimeMin,
  findLogForTrip,
  flightLogFromMission,
  upsertLogList,
} from "./flightlog";
import { convertFuel, formatFuelFigure, fuelUsedToUsGal, resolveAircraftFuelUnit, type FuelUnit } from "./fuelUnits";
import { createLogbookEntry, hoursFromMinutes, normalizeLogbookTimes } from "./logbook";
import { applyEngineMxHours, withResolvedStatus } from "./maintenance";
import type {
  AircraftSpec,
  DeskRole,
  FlightLogEntry,
  LogbookRole,
  MelItem,
  Mission,
  PilotLogbookEntry,
  Squawk,
} from "./types";

/**
 * SI / tracker prefill stays a hook. Live SimConnect hours + fuel live in
 * `draftPostFlightFromSim`. User edits win; sim actuals beat SimBrief planned.
 */
export function prefillPostFlightFromTracker(): null {
  return null;
}

/**
 * Mx estimate / fuel-burn forecasts read Wave E catalogs when the later form lands.
 * Desk must not invent money or hours here.
 */
export function estimateMxFromPostFlight(): null {
  return null;
}

export type PostFlightInput = {
  aircraftId: string;
  airTimeHours: number;
  engineHours: number;
  /** Per-engine tenths from the sim. Manual post-flight omits this. */
  engineHoursEach?: number[];
  apuHours: number;
  /** Legacy US-gal up/down. Prefer `fuelUpAmount` / `fuelDownAmount` + unit. */
  fuelUpGal?: number;
  fuelDownGal?: number;
  fuelUsedGal?: number;
  /** Fuel up/down/used in the tail’s Settings unit. */
  fuelUpAmount?: number;
  fuelDownAmount?: number;
  /** Fuel used in the tail’s Settings unit (preferred over `fuelUsedGal`). */
  fuelUsedAmount?: number;
  fuelUsedUnit?: FuelUnit;
  originIcao: string;
  destIcao: string;
  dateUtc?: string;
  crewId?: string;
  crewName: string;
  addLogbook: boolean;
  logbookRole?: LogbookRole;
  nightMin?: number;
  ifrMin?: number;
  notes?: string;
  flightLogId?: string;
  tripId?: string;
  role?: DeskRole;
};

export type PostFlightApplyInput = {
  fleet: AircraftSpec[];
  squawks: Squawk[];
  melItems: MelItem[];
  flightLogs: FlightLogEntry[];
  logbook: PilotLogbookEntry[];
  missions?: Mission[];
};

export type PostFlightResult = PostFlightApplyInput & {
  ok: boolean;
  error?: string;
  aircraft?: AircraftSpec;
  logbookEntry?: PilotLogbookEntry;
  flightLog?: FlightLogEntry;
  notice: string;
};

function amountToGal(amount: number | undefined, unit: FuelUnit, icaoType?: string): number | null {
  if (amount == null || !Number.isFinite(amount) || amount < 0) return null;
  return Math.round(fuelUsedToUsGal(amount, unit, fuelKindForType(icaoType)) * 10) / 10;
}

export function resolveFuelUsed(
  input: Pick<
    PostFlightInput,
    "fuelUpGal" | "fuelDownGal" | "fuelUsedGal" | "fuelUpAmount" | "fuelDownAmount" | "fuelUsedAmount" | "fuelUsedUnit"
  >,
  opts: { icaoType?: string; fuelUnit?: FuelUnit } = {},
): number | null {
  const unit = input.fuelUsedUnit ?? opts.fuelUnit ?? "gal";
  if (input.fuelUsedAmount != null && Number.isFinite(input.fuelUsedAmount) && input.fuelUsedAmount >= 0) {
    return amountToGal(input.fuelUsedAmount, unit, opts.icaoType);
  }
  if (input.fuelUsedGal != null && Number.isFinite(input.fuelUsedGal) && input.fuelUsedGal >= 0) {
    return Math.round(input.fuelUsedGal * 10) / 10;
  }
  if (
    input.fuelUpAmount != null &&
    input.fuelDownAmount != null &&
    Number.isFinite(input.fuelUpAmount) &&
    Number.isFinite(input.fuelDownAmount)
  ) {
    const up = amountToGal(input.fuelUpAmount, unit, opts.icaoType);
    const down = amountToGal(input.fuelDownAmount, unit, opts.icaoType);
    if (up == null || down == null) return null;
    return Math.round((up - down) * 10) / 10;
  }
  if (
    input.fuelUpGal != null &&
    input.fuelDownGal != null &&
    Number.isFinite(input.fuelUpGal) &&
    Number.isFinite(input.fuelDownGal)
  ) {
    return Math.round((input.fuelUpGal - input.fuelDownGal) * 10) / 10;
  }
  return null;
}

export function resolveFuelDownGal(
  input: Pick<PostFlightInput, "fuelDownGal" | "fuelDownAmount" | "fuelUsedUnit">,
  opts: { icaoType?: string; fuelUnit?: FuelUnit } = {},
): number | undefined {
  const unit = input.fuelUsedUnit ?? opts.fuelUnit ?? "gal";
  const fromAmount = amountToGal(input.fuelDownAmount, unit, opts.icaoType);
  if (fromAmount != null) return fromAmount;
  if (input.fuelDownGal != null && Number.isFinite(input.fuelDownGal) && input.fuelDownGal >= 0) {
    return Math.round(input.fuelDownGal * 10) / 10;
  }
  return undefined;
}

export function clampHours(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return 0;
  return Math.round(value * 100) / 100;
}

export function applyPostFlightTimes(
  spec: AircraftSpec,
  input: Pick<
    PostFlightInput,
    | "airTimeHours"
    | "engineHours"
    | "engineHoursEach"
    | "apuHours"
    | "fuelDownGal"
    | "fuelUsedGal"
    | "fuelUpGal"
    | "fuelDownAmount"
    | "fuelUsedUnit"
    | "destIcao"
  >,
  squawks: Squawk[] = [],
  melItems: MelItem[] = [],
  opts: { fuelUnit?: FuelUnit } = {},
): AircraftSpec {
  const air = clampHours(input.airTimeHours);
  const eng = clampHours(input.engineHours);
  const apu = clampHours(input.apuHours);
  const slotted = input.engineHoursEach?.length ? applyEngineMxHours(spec, input.engineHoursEach, eng) : null;
  const unit = resolveAircraftFuelUnit(spec, opts.fuelUnit);
  const fuelDown = resolveFuelDownGal(input, { icaoType: spec.icaoType, fuelUnit: unit });
  const next: AircraftSpec = {
    ...spec,
    airframeHours: Math.round((spec.airframeHours + air) * 100) / 100,
    engineHours: slotted
      ? slotted.engineHours
      : Math.round(((spec.engineHours ?? spec.airframeHours) + eng) * 100) / 100,
    engineHoursEach: slotted?.engineHoursEach ?? spec.engineHoursEach,
    engineCount: slotted?.engineCount ?? spec.engineCount,
    apuHours: Math.round(((spec.apuHours ?? 0) + apu) * 100) / 100,
    cycles: (spec.cycles ?? 0) + (air > 0 ? 1 : 0),
    lastAirportIcao: input.destIcao.trim().toUpperCase() || spec.lastAirportIcao,
    fuelOnBoardGal: fuelDown ?? spec.fuelOnBoardGal,
  };
  return withResolvedStatus(next, squawks, melItems);
}

export function applyPostFlight(
  slices: PostFlightApplyInput,
  input: PostFlightInput,
  now = new Date(),
): PostFlightResult {
  const air = clampHours(input.airTimeHours);
  const spec = slices.fleet.find((a) => a.id === input.aircraftId);
  if (!spec) {
    return { ...slices, ok: false, error: "Tail not on file.", notice: "POST-FLIGHT  tail not on file" };
  }
  if (air <= 0 && clampHours(input.engineHours) <= 0 && clampHours(input.apuHours) <= 0) {
    return { ...slices, ok: false, error: "Enter air, engine, or APU time.", notice: "POST-FLIGHT  no time entered" };
  }

  const unit = resolveAircraftFuelUnit(spec);
  const hintedLog = input.flightLogId
    ? slices.flightLogs.find((row) => row.id === input.flightLogId || row.tripId === input.flightLogId)
    : input.tripId
      ? findLogForTrip(slices.flightLogs, input.tripId)
      : undefined;
  const missionId = input.tripId || hintedLog?.tripId;
  const mission = missionId ? slices.missions?.find((row) => row.id === missionId) : undefined;
  const posted = mission?.postedHours;
  const eng = clampHours(input.engineHours);
  const apu = clampHours(input.apuHours);
  const dAir = posted ? Math.round((air - posted.airframe) * 100) / 100 : air;
  const dEng = posted ? Math.round((eng - posted.engine) * 100) / 100 : eng;
  const dApu = posted ? Math.round((apu - posted.apu) * 100) / 100 : apu;
  const addCycle = air > 0 && !posted?.cycle;
  const timed = applyPostFlightTimes(
    spec,
    { ...input, airTimeHours: 0, engineHours: 0, apuHours: 0, engineHoursEach: undefined },
    slices.squawks,
    slices.melItems,
    { fuelUnit: unit },
  );
  const nextSpec: AircraftSpec = withResolvedStatus(
    {
      ...timed,
      airframeHours: Math.max(0, Math.round((spec.airframeHours + dAir) * 100) / 100),
      engineHours: Math.max(0, Math.round(((spec.engineHours ?? spec.airframeHours) + dEng) * 100) / 100),
      apuHours: Math.max(0, Math.round(((spec.apuHours ?? 0) + dApu) * 100) / 100),
      cycles: (spec.cycles ?? 0) + (addCycle ? 1 : 0),
    },
    slices.squawks,
    slices.melItems,
  );
  const fleet = slices.fleet.map((a) => (a.id === spec.id ? nextSpec : a));
  const postedHours = {
    airframe: air,
    engine: eng,
    apu,
    cycle: Boolean(posted?.cycle || addCycle),
  };
  const missions = slices.missions?.map((row) =>
    mission && row.id === mission.id ? { ...row, hoursApplied: true, postedHours, crashBasis: undefined } : row,
  );

  let flightLogs = slices.flightLogs;
  let flightLog = input.flightLogId
    ? flightLogs.find((l) => l.id === input.flightLogId || l.tripId === input.flightLogId)
    : input.tripId
      ? findLogForTrip(flightLogs, input.tripId)
      : undefined;

  const originIcao = input.originIcao.trim().toUpperCase();
  const destIcao = input.destIcao.trim().toUpperCase();
  const stamp = dualClockStamp(now);

  if (flightLog) {
    const patched: FlightLogEntry = {
      ...flightLog,
      notes: input.notes?.trim() ? input.notes.trim() : flightLog.notes,
      originIcao: originIcao || flightLog.originIcao,
      destIcao: destIcao || flightLog.destIcao,
      route: originIcao && destIcao ? `${originIcao}–${destIcao}` : flightLog.route,
      updatedAtUtc: stamp.utc,
      updatedAtLocal: stamp.local,
    };
    flightLog = applyLogStatus(patched, "complete", now);
    flightLogs = upsertLogList(flightLogs, flightLog);
  } else if (input.tripId) {
    const mission = slices.missions?.find((m) => m.id === input.tripId);
    if (mission) {
      const created = flightLogFromMission(mission, {
        assignedPilot: input.crewName,
        role: input.role ?? "pilot",
        now,
      });
      flightLog = applyLogStatus(created, "complete", now);
      flightLogs = upsertLogList(flightLogs, flightLog);
    }
  }

  let logbook = slices.logbook;
  let logbookEntry: PilotLogbookEntry | undefined;
  if (input.addLogbook) {
    const block = flightLog ? blockTimeMin(flightLog) : null;
    const times = normalizeLogbookTimes({
      flightTimeMin: block != null && block > 0 ? block : Math.round(air * 60),
      nightMin: input.nightMin,
      ifrMin: input.ifrMin,
    });
    logbookEntry = createLogbookEntry({
      crewId: input.crewId || crewIdFromName(input.crewName),
      crewName: input.crewName,
      dateUtc: input.dateUtc || flightLog?.offBlocksUtc || now.toISOString(),
      originIcao: originIcao || flightLog?.originIcao || spec.lastAirportIcao || "",
      destIcao: destIcao || flightLog?.destIcao || "",
      registration: spec.registration,
      aircraftType: spec.icaoType,
      role: input.logbookRole ?? "PIC",
      flightTimeMin: times.flightTimeMin,
      airTimeMin: flightLog?.airTimeMin ?? Math.round(air * 60),
      landingsDay: flightLog?.landingsDay,
      landingsNight: flightLog?.landingsNight,
      nightMin: times.nightMin,
      ifrMin: times.ifrMin,
      notes: input.notes,
      flightLogId: flightLog?.id,
      now,
    });
    if (logbookEntry.originIcao && logbookEntry.destIcao) {
      const keep = logbookEntry.flightLogId
        ? logbook.filter((row) => row.flightLogId !== logbookEntry!.flightLogId)
        : logbook;
      logbook = [logbookEntry, ...keep];
    } else {
      logbookEntry = undefined;
    }
  }

  const fuel = resolveFuelUsed(input, { icaoType: spec.icaoType, fuelUnit: unit });
  const bits = [
    `POST-FLIGHT  ${spec.registration}`,
    `AF ${hoursFromMinutes(Math.round(air * 60)).toFixed(1)}`,
    `ENG ${clampHours(input.engineHours).toFixed(1)}`,
    `APU ${clampHours(input.apuHours).toFixed(1)}`,
  ];
  if (fuel != null) {
    bits.push(`FUEL ${formatFuelFigure(convertFuel(fuel, "gal", unit, fuelKindForType(spec.icaoType)), unit)}`);
  }
  if (logbookEntry) bits.push("LOGBOOK");

  return {
    fleet,
    squawks: slices.squawks,
    melItems: slices.melItems,
    flightLogs,
    logbook,
    missions,
    ok: true,
    aircraft: nextSpec,
    logbookEntry,
    flightLog,
    notice: bits.join("  ·  "),
  };
}
